<template>
  <div>
    <Modal :open.sync="showUserForm" :title="mode.title" size="sm:max-w-2xl">
      <div class="p-6">
        <form @submit.prevent="mode.function" class="space-y-4">
          <fieldset>
            <label for="name" class="form-label">Name</label>
            <input id="name" type="text" class="form-input" v-model="form.name">
          </fieldset>
          <fieldset>
            <label for="email" class="form-label">Email</label>
            <input id="email" type="email" class="form-input" v-model="form.email">
          </fieldset>
          <fieldset>
            <label for="role" class="form-label">Role</label>
            <select name="role" id="role" class="form-select" v-model="form.role">
              <option value="partner" v-if="account.role === 'admin'">Partner</option>
              <option value="owner">Owner</option>
              <option value="user">User</option>
            </select>
          </fieldset>
          <Collapse :active="form.role === 'user'">
            <fieldset>
              <label for="permissions" class="form-label">Permissions</label>
              <div class="grid grid-cols-2 gap-4 pt-1 pb-4">
                <label v-for="permission in visiblePermissions" :key="permission.value" class="flex">
                  <input type="checkbox" class="transform translate-y-px form-checkbox" :id="permission.value" :value="permission.value" v-model="form.permissions"  />
                  <div class="ml-2 leading-tight">
                    <div class="text-sm font-bold">
                      {{ permission.name }}
                    </div>
                    <div class="mt-1 text-xs text-gray-500">
                      {{ permission.description }}
                    </div>
                  </div>
                </label>
              </div>
            </fieldset>
          </Collapse>

          <div class="flex items-center justify-between">
            <button class="btn btn-primary" v-submitting="submitting">
              {{ mode.action }}
            </button>
            <button class="text-red-500" v-if="mode.title === 'Edit User'" @click="removeUser()" v-submitting="submitting">
              Remove
            </button>
          </div>
        </form>
      </div>
    </Modal>


    <div class="card">
      <div class="flex items-center justify-between py-6 card-header">
        <div>
          Users
        </div>
        <button class="btn btn-primary" @click="newUser">
          Add User
        </button>
      </div>

      <Loading v-if="loading" class="py-6" />

      <div v-else>
        <!-- No Results -->
        <div v-if="users.meta.total < 1" class="py-12 text-center">
          <p class="mb-8 text-lg font-bold">
            This account doesn't have any users.
          </p> 
          <button class="btn btn-secondary" @click="newUser">
            Add User
          </button>
        </div>

        <!-- Results -->
        <div v-else>
          <!-- Table -->
          <div class="flex flex-col overflow-hidden">
            <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div class="inline-block min-w-full overflow-hidden align-middle rounded-t-lg">
                <table class="min-w-full">
                  <thead>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Name
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Email
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                      Role
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b whitespace-nowrap bg-gray-50">
                    </th>
                  </thead>
                  <tbody>
                    <tr v-for="user in users.data" :key="user.id" class="cursor-pointer hover:bg-gray-50">
                      <td class="flex items-center py-4 pl-5 pr-6 font-bold border-b whitespace-nowrap">
                        <avatar :username="user.name" :size="36" color="#fff" background-color="#718096" class="mr-4" :custom-style="{ 'font-weight': 'bold' }" /> 
                        {{ user.name }}
                      </td>
                      <td class="px-6 py-4 border-b whitespace-nowrap">
                        {{ user.email }}
                      </td>
                      <td class="px-6 py-4 border-b whitespace-nowrap">
                        <badge class="text-gray-600 bg-gray-100">
                          {{ user.accounts[0].role | capitalize }}
                        </badge>
                      </td>
                      <td class="px-6 py-4 text-right border-b whitespace-nowrap">
                        <button class="btn btn-sm btn-secondary" @click="editUser(user)">
                          Edit
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="users.meta.total" :per-page="users.meta.per_page" :current-page="users.meta.current_page" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Avatar from 'vue-avatar'

export default {
  name: 'SettingsUsers',

  metaInfo: {
    title: 'User Settings'
  },

  components: {
    Avatar
  },

  created () {
    this.getUsers()
  },

  computed: {
    ...mapGetters(['account']),

    visiblePermissions () {
      let permissions = ['account', 'assets', 'billing', 'users', 'reports']
      if (this.account.subscription.automations) { 
        permissions.push('automations')
        permissions.push('monitor') 
      }
      if (this.account.subscription.orders) { 
        permissions.push('orders') 
      }
      if (this.account.subscription.records) { 
        permissions.push('records') 
      }
      return this.permissions.filter(perm => permissions.indexOf(perm.value) > -1)
    }
  },

  data () {
    return {
      loading: true,
      submitting: false,
      showUserForm: false,
      mode: {
        title: 'Add User',
        function: this.createUser,
        action: 'Add User'
      },
      defaultForm: {
        name: '',
        password: '',
        email: '',
        role: 'user',
        permissions: ['automations', 'monitor', 'orders', 'records', 'reports']
      },
      form: {
        name: '',
        password: '',
        email: '',
        role: 'user',
        permissions: ['automations', 'monitor', 'orders', 'records', 'reports']
      },
      permissions: [
        { 
          name: 'Account', 
          value: 'account',
          description: 'Can view and change account-wide settings like company and integration details.',
          requires: null
        },
        { 
          name: 'Assets', 
          value: 'assets',
          description: 'Can view and sync assets like areas, vehicles, drivers, and groups.',
          requires: null
        },
        { 
          name: 'Billing', 
          value: 'billing',
          description: 'Can view and change billing amounts and payment details.',
          requires: null
        },
        { 
          name: 'Users', 
          value: 'users',
          description: 'Can view, add, update, and remove users from the account.',
          requires: null
        },
        { 
          name: 'Automations', 
          value: 'automations',
          description: 'Access to automations and associated features like logs, monitor, and digests.',
          requires: 'automations'
        },
        { 
          name: 'Monitor', 
          value: 'monitor',
          description: 'Can access the monitor dashboard and acknowledge alerts pushed through to its feed.',
          requires: 'monitor'
        },
        { 
          name: 'Orders', 
          value: 'orders',
          description: 'Access to orders and associated features like batch uploader and notifications.',
          requires: 'orders'
        },
        { 
          name: 'Records', 
          value: 'records',
          description: 'Access to records and associated features like vendors, files and categories.',
          requires: 'records'
        },
        { 
          name: 'Reports', 
          value: 'reports',
          description: 'Can view and pull reports as well as create and schedule new ones.',
          requires: null
        }
      ],
      users: []
    }
  },

  methods: {
    newUser () {
      this.mode = {
        title: 'Add User',
        function: this.createUser,
        action: 'Add User'
      }
      this.form = JSON.parse(JSON.stringify(this.defaultForm))
      this.showUserForm = true
    },

    editUser (user) {
      this.mode = {
        title: 'Edit User',
        function: this.updateUser,
        action: 'Save Changes'
      }

      let form = {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.accounts[0].role,
        permissions: []
      }

      for (let key in user.accounts[0].permissions) {
        if (user.accounts[0].permissions[key]) {
          form.permissions.push(key)
        }
      }
      
      this.form = JSON.parse(JSON.stringify(form))
      this.showUserForm = true
    },

    async getUsers () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/users`)
        this.users = data.users
        this.loading = false
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      }
    },

    async createUser () {
      try {
        this.submitting = true
        await this.$api.post(`account/${this.$route.params.account}/users`, this.form)
        this.showUserForm = false
        this.form = JSON.parse(JSON.stringify(this.defaultForm))
        this.getUsers()
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    async updateUser () {
      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/users/${this.form.id}`, this.form)
        this.showUserForm = false
        this.form = JSON.parse(JSON.stringify(this.defaultForm))
        this.getUsers()
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    async removeUser () {
      if (window.confirm('Are you sure you want to remove this user from your account?')) {
        try {
          this.submitting = true
          await this.$api.delete(`account/${this.$route.params.account}/users/${this.form.id}`)
          this.getUsers()
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    }
  },

  watch: {
    'form.role': {
      handler (newVal) {
        if (newVal === 'partner' || newVal === 'owner' || newVal === 'admin') {
          this.form.permissions = [
            'account', 
            'assets', 
            'automations', 
            'billing',
            'monitor',
            'orders', 
            'records',
            'users',
            'reports'
          ]
        } else if (newVal === 'user') {
          this.form.permissions = this.defaultForm.permissions
        }
      }
    }
  }
}
</script>